import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import authService from '../authService.js';
import { env, validateEnv } from '../../env.config.js';
import { handleApiError, withRetry } from '../errorService.js';
import { toastService } from '../toastService.js';
import { logger } from '../../utils/logger.js';
import { StorageUtils } from '../../utils/StorageUtils.js';
import type { ApiError } from '../../types/api.js';



export class ApiService {
    private static instance: ApiService;
    private readonly axios: AxiosInstance;
    private isRefreshing = false;
    private refreshSubscribers: Array<(token: string) => void> = [];
    private static serverTimeOffset = 0; // Décalage en ms entre client et serveur

    // ✅ ROUTES PUBLIQUES - Liste claire et documentée pour exclure du token et du refresh automatique
    private readonly publicRoutes = [
        '/api/auth/login',
        '/api/v1/clinic/auth/login',
        '/api/auth/logout',
        '/api/auth/refresh',
        '/api/auth/password/forgot',
        '/api/auth/password/reset'
    ];

    // ✅ ROUTES SILENCIEUSES - Requêtes d'arrière-plan qui ne doivent pas déclencher de Toast d'erreur
    private readonly backgroundRoutes = [
        '/api/auth/verify',
        '/api/auth/refresh',
        '/api/user-status/heartbeat',
        '/api/dashboard/system/metrics',
        '/api/monitoring/batch',
        '/api/teleconsult/waiting-room/join'
    ];

    private constructor() {
        validateEnv();

        // 🌐 LOGIQUE DYNAMIQUE : Utiliser proxy relatif si URL vide
        const useRelativeProxy = !env.API_BASE_URL || env.API_BASE_URL === '';

        this.axios = axios.create({
            baseURL: '', // Toujours utiliser proxy relatif pour éviter CORS
            timeout: 10000, // Réduit de 30s à 10s pour éviter timeouts longs
            withCredentials: true, // For HttpOnly cookies
            xsrfCookieName: 'csrf-token',
            xsrfHeaderName: 'x-csrf-token'
        });

        logger.debug(`[ApiService] Configuration: ${useRelativeProxy ? 'PROXY RELATIF (/api/*)' : 'URL ABSOLUE'} - BaseURL: ${env.API_BASE_URL || 'RELATIVE'}`);

        this.setupInterceptors();
    }

    public static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }
        return ApiService.instance;
    }

    private setupInterceptors(): void {
        // Request Interceptor
        this.axios.interceptors.request.use(
            (config) => {
                // ✅ LOGIQUE SIMPLIFIÉE: Exclure token pour routes publiques
                const isPublicRoute = this.publicRoutes.some(route => config.url?.includes(route));

                if (!isPublicRoute) {
                    // 🔐 Ajouter token pour routes protégées
                    const token = StorageUtils.getToken();
                    if (token && config.headers) {
                        config.headers['Authorization'] = `Bearer ${token}`;
                    }

                    // 🏥 Contexte Clinique: Isolation multi-tenant stricte
                    const activeClinicId = localStorage.getItem('activeClinicId');
                    if (activeClinicId && config.headers && !config.headers['X-Clinic-ID']) {
                        config.headers['X-Clinic-ID'] = activeClinicId;
                    }
                }

                // 📊 Headers performance (optionnel - peut être désactivé en prod)
                if (config.headers) {
                    config.headers['X-Bundle-Version'] = '1.2.0-optimized';
                    // Supprimé : 'X-Frontend-Type' causait erreurs CORS

                    // Métriques de performance si disponibles
                    const performanceData = this.getPerformanceMetrics();
                    if (performanceData) {
                        config.headers['X-Render-Time'] = performanceData.renderTime.toString();
                        config.headers['X-Load-Time'] = performanceData.loadTime.toString();
                    }

                    // 🛠️ PHASE 2 STRATÉGIQUE: Interceptor Global Content-Type
                    const method = config.method?.toLowerCase();
                    if (['post', 'put', 'patch'].includes(method || '')) {
                        if (config.data instanceof FormData) {
                            // Pour FormData, NE PAS définir Content-Type
                            // Axios l'ajoutera automatiquement avec le boundary
                            logger.debug(`[ApiService] FormData detected for ${config.url}, NOT setting Content-Type header`);
                            // IMPORTANT: Ne pas supprimer, ni ajouter Content-Type
                            if (config.headers && 'Content-Type' in config.headers) {
                                delete config.headers['Content-Type'];
                            }
                            // S'assurer que les headers existent
                            if (!config.headers) {
                                config.headers = new AxiosHeaders();
                            }
                        } else {
                            config.headers['Content-Type'] = 'application/json';
                            // Si data est absent, on injecte {} pour forcer Axios à envoyer le header
                            if (config.data === undefined || config.data === null) {
                                config.data = {};
                            }
                        }
                    }
                    
                    logger.debug(`[ApiService] Request headers for ${config.url}:`, {
                        method: config.method?.toUpperCase(),
                        'Content-Type': config.headers['Content-Type'],
                        hasAuth: !!config.headers['Authorization'],
                        isFormData: config.data instanceof FormData,
                    });

                    // 🛡️ CSRF Protection: Double-Submit Cookie Pattern (Regex-based recovery with storage fallback)
                    let csrfToken = typeof StorageUtils?.getCsrfToken === 'function' ? StorageUtils.getCsrfToken() : null;
                    if (!csrfToken && typeof document !== 'undefined' && typeof document.cookie === 'string') {
                        const match = document.cookie.match(new RegExp('(^|;\\s*)csrf-token\\s*=\\s*([^;]+)', 'i'));
                        csrfToken = match ? match[2].trim() : null;
                    }
                    if (csrfToken) {
                        config.headers['x-csrf-token'] = csrfToken;
                        config.headers['x-xsrf-token'] = csrfToken;
                    }
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response Interceptor
        this.axios.interceptors.response.use(
            (response) => {
                // 🕒 SYNC TEMPORELLE : Capturer le décalage via le header Date du serveur
                const serverDateHeader = response.headers['date'];
                if (serverDateHeader) {
                    const serverTime = new Date(serverDateHeader).getTime();
                    const clientTime = Date.now();
                    ApiService.serverTimeOffset = serverTime - clientTime;
                    
                    if (Math.abs(ApiService.serverTimeOffset) > 5000) {
                        logger.debug(`[ApiService] Clock Skew détecté: ${ApiService.serverTimeOffset}ms`);
                    }
                }
                return response;
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

                // Ne pas tenter de rafraîchir pour les routes publiques/d'authentification ou la vérification de session initiale
                const isPublicRoute = originalRequest.url && this.publicRoutes.some(route => originalRequest.url?.includes(route));
                const isVerifyRoute = originalRequest.url?.includes('/api/auth/verify');

                if (error.response?.status === 401 && !originalRequest._retry && !isPublicRoute && !isVerifyRoute) {
                    originalRequest._retry = true;

                    // Si un rafraîchissement est déjà en cours, mettre en attente la requête originale
                    if (this.isRefreshing) {
                        return new Promise((resolve) => {
                            this.refreshSubscribers.push((token: string) => {
                                if (originalRequest.headers) {
                                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                                }
                                resolve(this.axios(originalRequest));
                            });
                        });
                    }

                    this.isRefreshing = true;

                    try {
                        // Appeler endpoint refresh (cookies envoyés automatiquement)
                        const refreshResponse = await authService.refreshToken();
                        if (!refreshResponse || !refreshResponse.token) {
                            throw new Error('Refresh token invalide ou expiré');
                        }

                        // Résoudre les requêtes en attente
                        this.onRefreshSuccess(refreshResponse.token);

                        if (originalRequest.headers) {
                            originalRequest.headers['Authorization'] = `Bearer ${refreshResponse.token}`;
                        }
                        return this.axios(originalRequest);
                    } catch (refreshError) {
                        this.onRefreshFailure(refreshError as Error);
                        return Promise.reject(refreshError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                // Use handleApiError from errorService for consistent error handling
                const url = originalRequest.url || 'unknown';
                const isBackgroundRoute = this.backgroundRoutes.some(route => url.includes(route));
                
                if (!isBackgroundRoute) {
                    logger.error(`❌ [ApiService] Request failed: ${url}`, { status: error.response?.status });
                }

                const serviceError = handleApiError(error, (message, type) => {
                    if (!isBackgroundRoute) {
                        toastService.show(`${message} (${url})`, type);
                    }
                });
                return Promise.reject(serviceError);
            }
        );
    }

    private onRefreshSuccess(token: string): void {
        // Mettre à jour le token pour le service de sockets pour éviter le rejet des reconnexions
        // Importation dynamique pour briser la dépendance circulaire au chargement
        import('../unifiedSocketService.js')
            .then((module) => {
                const sService = module.default || module.socketService;
                if (sService && typeof sService.setAuthToken === 'function') {
                    sService.setAuthToken(token);
                    logger.info('[ApiService] WebSocket token successfully synchronized after RTR rotation');
                }
            })
            .catch((err) => {
                logger.warn('[ApiService] Could not sync token with socketService on refresh success:', err);
            });

        this.refreshSubscribers.forEach(callback => callback(token));
        this.refreshSubscribers = [];
    }

    private onRefreshFailure(error: Error): void {
        logger.error('Failed to refresh token:', error);
        this.refreshSubscribers = [];
        // ✅ CORRECTION: Nettoyer TOUT le storage (token + user + refreshToken)
        // Avant, seul USER_STORAGE_KEY était supprimé, laissant un token stale
        // qui faisait croire au ProtectedRoute que l'utilisateur était connecté
        // → redirection vers /unauthorized au lieu de /login
        StorageUtils.clearAll();
        localStorage.removeItem('refreshToken');
        
        // 🛡️ Éviter une boucle de rechargement infinie si l'utilisateur est déjà sur une page d'authentification
        const currentPath = window.location.pathname;
        const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].some(
            path => currentPath.startsWith(path)
        );
        if (!isAuthPage) {
            window.location.href = '/login';
        }
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return withRetry(async () => {
            logger.debug(`🌐 [ApiService] GET ${url}`);
            const response = await this.axios.get<T>(url, config);
            logger.debug(`✅ [ApiService] GET ${url} - Status: ${response.status}`);
            return response.data;
        }, 2, 1000); // 2 tentatives, délai de 1s
    }

    public async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        return withRetry(async () => {
            const response = await this.axios.post<T>(url, data, config);
            return response.data;
        }, 2, 1000); // 2 tentatives, délai de 1s
    }

    public async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        return withRetry(async () => {
            const response = await this.axios.put<T>(url, data, config);
            return response.data;
        }, 2, 1000); // 2 tentatives, délai de 1s
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return withRetry(async () => {
            const response = await this.axios.delete<T>(url, config);
            return response.data;
        }, 2, 1000); // 2 tentatives, délai de 1s
    }

    public async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        return withRetry(async () => {
            const response = await this.axios.patch<T>(url, data, config);
            return response.data;
        }, 2, 1000); // 2 tentatives, délai de 1s
    }

    // ============================
    // RESPONSE EXTRACTION HELPERS (non-breaking)
    // ============================

    /**
     * Helper pour extraire automatiquement response.data
     * Favorise le standard ApiResponse<T> ({ status: 'success', data: T }) du BaseController.
     */
    private extractResponseData<T>(response: unknown, context: string): T {
        if (response && typeof response === 'object') {
            const respObj = response as Record<string, unknown>;
            
            // ✅ STANDARD: Format dicté par BaseController
            if (respObj.status === 'success' && 'data' in respObj && respObj.data !== undefined) {
                return respObj.data as T;
            }

            // Fallback: Si un 'data' direct existe (sans 'status')
            if ('data' in respObj) {
                return respObj.data as T;
            }

            // Fallback: Clés plurielles ou spécifiques du domaine
            const collectionKeys = ['users', 'appointments', 'prescriptions', 'records', 'notifications', 'invoices', 'consultations'];
            for (const key of collectionKeys) {
                if (key in respObj && Array.isArray(respObj[key])) {
                    return respObj[key] as T;
                }
            }

            // Fallback final
            if (Array.isArray(response) || 'user' in respObj || 'appointment' in respObj || 'consultation' in respObj) {
                return response as T;
            }
        }

        logger.debug(`[${context}] Format de réponse non standard (hors ApiResponse<T>), renvoyé brut.`);
        return response as T;
    }

    public async getExtracted<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.get<unknown>(url, config);
        return this.extractResponseData<T>(response, `GET ${url}`);
    }

    public async postExtracted<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.post<unknown, D>(url, data, config);
        return this.extractResponseData<T>(response, `POST ${url}`);
    }

    public async putExtracted<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.put<unknown, D>(url, data, config);
        return this.extractResponseData<T>(response, `PUT ${url}`);
    }

    public async patchExtracted<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.patch<unknown, D>(url, data, config);
        return this.extractResponseData<T>(response, `PATCH ${url}`);
    }

    public async deleteExtracted<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.delete<unknown>(url, config);
        return this.extractResponseData<T>(response, `DELETE ${url}`);
    }
    private normalizeResponse<T>(raw: unknown): { status: 'success' | 'error'; data: T | null; error?: import('../../types/api').ApiError } {
        // Accept common server formats and unify
        if (raw && typeof raw === 'object') {
            const obj = raw as Record<string, unknown>;
            if ('status' in obj && 'data' in obj) {
                const status = (obj.status === 'success' || obj.status === 'ok') ? 'success' : (obj.status === 'error' ? 'error' : 'success');
                return { status, data: obj.data as T };
            }
            if ('user' in obj || 'users' in obj) {
                return { status: 'success', data: raw as T };
            }
        }
        return { status: 'success', data: (raw as T) ?? null };
    }

    public async getNorm<T>(url: string, config?: AxiosRequestConfig): Promise<{ status: 'success' | 'error'; data: T | null; error?: ApiError }> {
        const data = await this.get<unknown>(url, config);
        return this.normalizeResponse<T>(data);
    }

    public async postNorm<T, D = unknown>(url: string, body?: D, config?: AxiosRequestConfig): Promise<{ status: 'success' | 'error'; data: T | null; error?: ApiError }> {
        const data = await this.post<unknown, D>(url, body, config);
        return this.normalizeResponse<T>(data);
    }

    public async putNorm<T, D = unknown>(url: string, body?: D, config?: AxiosRequestConfig): Promise<{ status: 'success' | 'error'; data: T | null; error?: ApiError }> {
        const data = await this.put<unknown, D>(url, body, config);
        return this.normalizeResponse<T>(data);
    }

    public async patchNorm<T, D = unknown>(url: string, body?: D, config?: AxiosRequestConfig): Promise<{ status: 'success' | 'error'; data: T | null; error?: ApiError }> {
        const data = await this.patch<unknown, D>(url, body, config);
        return this.normalizeResponse<T>(data);
    }

    public async deleteNorm<T>(url: string, config?: AxiosRequestConfig): Promise<{ status: 'success' | 'error'; data: T | null; error?: ApiError }> {
        const data = await this.delete<unknown>(url, config);
        return this.normalizeResponse<T>(data);
    }
    // Méthode pour obtenir l'heure synchronisée avec le serveur
    public static getServerTime(): number {
        return Date.now() + ApiService.serverTimeOffset;
    }

    // Méthode pour obtenir les métriques de performance
    private getPerformanceMetrics(): { renderTime: number; loadTime: number; errorCount: number } | null {
        try {
            // Récupérer les métriques depuis le localStorage ou performance API
            const storedMetrics = localStorage.getItem('ekose_performance_metrics');
            if (storedMetrics) {
                return JSON.parse(storedMetrics);
            }

            // Métriques de base depuis Performance API
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            if (navigation) {
                return {
                    renderTime: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
                    loadTime: Math.round(navigation.loadEventEnd - navigation.fetchStart),
                    errorCount: parseInt(localStorage.getItem('ekose_error_count') || '0')
                };
            }
        } catch (error) {
            logger.warn('Erreur récupération métriques performance:', error);
        }
        return null;
    }
}
